/**
 * SettingsRbacSection — `/settings` 真实 RBAC 模块 (Issue #243).
 *
 * 真相边界：
 *   - 全部数据来自 #242 OpenAPI/api-client：预置角色、权限矩阵、reveal/export
 *     默认额度与当前账号窗口。所有写入均调用真实 RBAC 接口；前端不维护乐观状态。
 *   - 普通管理员只读：不渲染绑定 / 撤销按钮和对话框，避免操作人误以为本地可改。
 *   - 超级管理员（`*:*` 权限）可见绑定入口。绑定提交必须由操作人手工填写：原因、
 *     审批单号、是否通过短信二次验证；高危预置角色必须开启二次验证勾选并填写
 *     审批单号，否则提交按钮禁用。
 *   - Dialog 写入组件由父级 `key` 重新挂载，防止上一次表单状态残留。
 *   - 渠道商内部后台登录策略以受控只读状态展示，第一版关闭。
 */
import { useMemo, useState, type ReactElement } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useSettingsRbacCatalog,
  useUserRoleBindings,
  type CreateRoleBindingRequestDto,
  type PermissionMatrixResponseDto,
  type PresetRoleDto,
  type PresetRoleListResponseDto,
  type RevealExportQuotaResponseDto,
  type RoleBindingDto,
} from '@/hooks/useSettingsRbac';
import {
  canManageRoleBindings,
  isHighRiskRiskLevel,
} from '@/lib/rbacPermissions';
import RoleBindingDialog from './RoleBindingDialog';

const REVOCATION_REASON_PLACEHOLDER = '例如：员工离职，回收 RBAC 权限';

export interface SettingsRbacSectionProps {
  readonly currentUserId: string | null;
  readonly currentPermissions: readonly string[];
}

export default function SettingsRbacSection({
  currentUserId,
  currentPermissions,
}: SettingsRbacSectionProps): ReactElement {
  const catalog = useSettingsRbacCatalog();
  const canManage = canManageRoleBindings(currentPermissions);

  return (
    <section className="space-y-3" data-testid="settings-rbac-section">
      <header>
        <h3 className="text-lg font-medium">角色与权限矩阵管理</h3>
        <p className="text-sm text-muted-foreground">
          数据来自后端 #242 RBAC 契约：预置角色目录、权限矩阵、reveal/export 默认额度，
          以及当前账号的角色绑定。第一版不开放自定义角色与租户/机构编辑。普通管理员只读；
          仅超级管理员（拥有 <code>*:*</code> 权限）可发起绑定/撤销。
        </p>
      </header>

      <RbacReadOnlyBanner canManage={canManage} />

      <PresetRolesCard roles={catalog.presetRoles.data} loading={catalog.presetRoles.loading} error={catalog.presetRoles.error} />
      <PermissionMatrixCard matrix={catalog.matrix.data} loading={catalog.matrix.loading} error={catalog.matrix.error} />
      <QuotaCard quota={catalog.quota.data} loading={catalog.quota.loading} error={catalog.quota.error} />

      <RoleBindingsCard
        canManage={canManage}
        currentUserId={currentUserId}
        roles={catalog.presetRoles.data}
      />

      <ChannelPartnerPolicyCard />
    </section>
  );
}

// -------------------------------------------------------------------------
// Read-only banner
// -------------------------------------------------------------------------

interface RbacReadOnlyBannerProps {
  readonly canManage: boolean;
}

function RbacReadOnlyBanner({ canManage }: RbacReadOnlyBannerProps): ReactElement {
  if (canManage) {
    return (
      <div
        className="rounded-md border border-amber-500/60 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300"
        data-testid="settings-rbac-super-banner"
      >
        当前账号具备 <code>*:*</code> 权限，可发起角色绑定。所有写入操作均会写入不可变审计：操作人、被变更人、变更前后快照、原因、审批单号、IP、设备、二次验证结果。
      </div>
    );
  }
  return (
    <div
      className="rounded-md border border-dashed bg-muted/40 p-3 text-xs text-muted-foreground"
      data-testid="settings-rbac-read-only-banner"
    >
      当前账号无 <code>*:*</code> 权限，本节为只读视图。无法在前端发起角色绑定、变更或撤销。
    </div>
  );
}

// -------------------------------------------------------------------------
// Preset roles
// -------------------------------------------------------------------------

interface PresetRolesCardProps {
  readonly roles: PresetRoleListResponseDto | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function PresetRolesCard({ roles, loading, error }: PresetRolesCardProps): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">预置角色目录</CardTitle>
        <CardDescription>
          第一版不开放后台自定义角色创建。下表来自 <code>GET /api/v1/auth/roles</code>。
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground" data-testid="settings-rbac-roles-loading">
            加载中…
          </div>
        ) : error ? (
          <div className="text-sm text-destructive" data-testid="settings-rbac-roles-error">
            {error}
          </div>
        ) : !roles || roles.roles.length === 0 ? (
          <div
            className="rounded border border-dashed py-6 text-center text-sm text-muted-foreground"
            data-testid="settings-rbac-roles-empty"
          >
            暂无预置角色。
          </div>
        ) : (
          <Table data-testid="settings-rbac-roles-table">
            <TableHeader>
              <TableRow>
                <TableHead>角色 key</TableHead>
                <TableHead>名称</TableHead>
                <TableHead>风险级别</TableHead>
                <TableHead>默认数据范围</TableHead>
                <TableHead>内部登录</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.roles.map((role) => (
                <TableRow key={role.key} data-testid={`settings-rbac-role-row-${role.key}`}>
                  <TableCell className="font-mono text-xs">{role.key}</TableCell>
                  <TableCell>{role.displayName}</TableCell>
                  <TableCell>
                    <RiskBadge level={role.riskLevel} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{role.defaultDataScope}</TableCell>
                  <TableCell className="text-xs">
                    {role.internalAdminLoginAllowed ? '允许' : '禁止'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function RiskBadge({ level }: { level: PresetRoleDto['riskLevel'] }): ReactElement {
  const high = isHighRiskRiskLevel(level);
  const label = ({
    low: '低',
    medium: '中',
    high: '高',
    critical: '极高',
  } as const)[level];
  return (
    <span
      className={
        high
          ? 'rounded bg-destructive/10 px-2 py-0.5 text-xs text-destructive'
          : 'rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground'
      }
      data-testid={`settings-rbac-risk-${level}`}
    >
      {label}
    </span>
  );
}

// -------------------------------------------------------------------------
// Permission matrix
// -------------------------------------------------------------------------

interface PermissionMatrixCardProps {
  readonly matrix: PermissionMatrixResponseDto | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function PermissionMatrixCard({
  matrix,
  loading,
  error,
}: PermissionMatrixCardProps): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">模块 / 页面 / 字段权限矩阵</CardTitle>
        <CardDescription>
          管理员确认后的默认矩阵，前端只读展示。
          来自 <code>GET /api/v1/auth/permissions/matrix</code>。
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground" data-testid="settings-rbac-matrix-loading">
            加载中…
          </div>
        ) : error ? (
          <div className="text-sm text-destructive" data-testid="settings-rbac-matrix-error">
            {error}
          </div>
        ) : !matrix || matrix.modules.length === 0 ? (
          <div
            className="rounded border border-dashed py-6 text-center text-sm text-muted-foreground"
            data-testid="settings-rbac-matrix-empty"
          >
            暂无权限矩阵数据。
          </div>
        ) : (
          <div className="space-y-4" data-testid="settings-rbac-matrix">
            {matrix.modules.map((module) => (
              <div
                key={module.moduleKey}
                className="rounded border bg-muted/20 p-3"
                data-testid={`settings-rbac-matrix-module-${module.moduleKey}`}
              >
                <div className="mb-2 flex items-baseline justify-between">
                  <div>
                    <div className="text-sm font-medium">{module.displayName}</div>
                    <div className="font-mono text-xs text-muted-foreground">{module.moduleKey}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  {module.pages.map((page) => (
                    <div
                      key={page.pageKey}
                      className="rounded border bg-background p-3"
                      data-testid={`settings-rbac-matrix-page-${page.pageKey}`}
                    >
                      <div className="mb-1 flex items-baseline justify-between">
                        <div className="text-sm font-medium">{page.displayName}</div>
                        <div className="font-mono text-xs text-muted-foreground">{page.pageKey}</div>
                      </div>
                      {page.defaultVisibleRoles.length > 0 ? (
                        <p className="text-xs text-muted-foreground">
                          默认可见角色：{page.defaultVisibleRoles.join('、')}
                        </p>
                      ) : null}
                      {page.highRiskActions.length > 0 ? (
                        <div className="mt-2">
                          <div className="text-xs font-medium">高危动作</div>
                          <ul className="ml-4 list-disc text-xs">
                            {page.highRiskActions.map((action) => (
                              <li key={action.action}>
                                <span className="font-mono">{action.action}</span>（
                                <RiskBadge level={action.riskLevel} />）{action.note}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {page.fieldControls.length > 0 ? (
                        <div className="mt-2">
                          <div className="text-xs font-medium">字段级控制</div>
                          <ul className="ml-4 list-disc text-xs">
                            {page.fieldControls.map((field) => (
                              <li key={field.field}>
                                <span className="font-mono">{field.field}</span> → {field.defaultRule}
                                {field.note ? `（${field.note}）` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -------------------------------------------------------------------------
// Reveal/export quota
// -------------------------------------------------------------------------

interface QuotaCardProps {
  readonly quota: RevealExportQuotaResponseDto | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function QuotaCard({ quota, loading, error }: QuotaCardProps): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Reveal / Export 默认额度</CardTitle>
        <CardDescription>
          默认每日 / 每月额度由管理员确认；任何修改必须经超级管理员通过真实后端契约执行并写入审计。
          数据来自 <code>GET /api/v1/auth/reveal-export-quota</code>。
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground" data-testid="settings-rbac-quota-loading">
            加载中…
          </div>
        ) : error ? (
          <div className="text-sm text-destructive" data-testid="settings-rbac-quota-error">
            {error}
          </div>
        ) : !quota ? (
          <div
            className="rounded border border-dashed py-6 text-center text-sm text-muted-foreground"
            data-testid="settings-rbac-quota-empty"
          >
            暂无额度数据。
          </div>
        ) : !quota.available ? (
          // Issue #279: backend returned a controlled unavailable snapshot
          // (`available=false` with a structured reason + Chinese message).
          // Render it as a controlled disabled state — show the
          // administrator-confirmed default limits so the operator still
          // sees the numbers, but call out that the live counters are
          // not reachable and reveal/export is blocked. Do NOT synthesize
          // remaining/used values on the frontend.
          <div
            className="space-y-2 rounded border border-amber-500/60 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300"
            data-testid="settings-rbac-quota-unavailable"
          >
            <div className="font-medium">Reveal / Export 额度暂不可用</div>
            <div data-testid="settings-rbac-quota-unavailable-message">
              {quota.message ?? '后端 reveal/export 额度审计依赖暂时不可用，所有 reveal/export 操作均已按受控不可用状态阻止。'}
            </div>
            <div className="font-mono text-[10px] text-muted-foreground">
              <span data-testid="settings-rbac-quota-unavailable-reason">reason={quota.reason}</span>
              <span className="mx-1">·</span>
              <span>
                默认限额 每日 {quota.limits.daily} / 每月 {quota.limits.monthly}
              </span>
            </div>
          </div>
        ) : (
          <dl
            className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2"
            data-testid="settings-rbac-quota-grid"
          >
            <div>
              <dt className="text-xs uppercase text-muted-foreground">默认每日额度</dt>
              <dd data-testid="settings-rbac-quota-daily-limit">{quota.limits.daily}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">默认每月额度</dt>
              <dd data-testid="settings-rbac-quota-monthly-limit">{quota.limits.monthly}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">当前日窗口已用 / 剩余</dt>
              <dd data-testid="settings-rbac-quota-daily-used">
                {quota.daily.used} / {quota.daily.remaining}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">当前月窗口已用 / 剩余</dt>
              <dd data-testid="settings-rbac-quota-monthly-used">
                {quota.monthly.used} / {quota.monthly.remaining}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">是否耗尽</dt>
              <dd data-testid="settings-rbac-quota-exhausted">{quota.exhausted ? '是' : '否'}</dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

// -------------------------------------------------------------------------
// Role bindings (super-admin only writable)
// -------------------------------------------------------------------------

interface RoleBindingsCardProps {
  readonly canManage: boolean;
  readonly currentUserId: string | null;
  readonly roles: PresetRoleListResponseDto | null;
}

function RoleBindingsCard({
  canManage,
  currentUserId,
  roles,
}: RoleBindingsCardProps): ReactElement {
  const [targetUserInput, setTargetUserInput] = useState('');
  const [activeTargetUserId, setActiveTargetUserId] = useState<string | null>(currentUserId);
  const [dialogToken, setDialogToken] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const bindings = useUserRoleBindings(activeTargetUserId);

  function applyTargetFromInput(): void {
    const trimmed = targetUserInput.trim();
    setActiveTargetUserId(trimmed.length > 0 ? trimmed : null);
  }

  function useMyId(): void {
    if (!currentUserId) return;
    setTargetUserInput(currentUserId);
    setActiveTargetUserId(currentUserId);
  }

  function openDialog(): void {
    setDialogToken((t) => t + 1);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
  }

  async function handleCreate(input: CreateRoleBindingRequestDto): Promise<void> {
    await bindings.createBinding(input);
    setDialogOpen(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">用户角色绑定</CardTitle>
        <CardDescription>
          仅超级管理员可发起 / 撤销绑定。普通管理员看到的为只读列表。绑定列表来自
          <code> GET /api/v1/auth/users/&#123;userId&#125;/role-bindings</code>。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="settings-rbac-user-input">目标用户 ID</Label>
            <Input
              id="settings-rbac-user-input"
              value={targetUserInput}
              onChange={(e) => setTargetUserInput(e.target.value)}
              placeholder="usr_..."
              className="w-72"
              data-testid="settings-rbac-user-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={applyTargetFromInput}
            data-testid="settings-rbac-user-apply"
          >
            查询绑定
          </Button>
          <Button
            variant="ghost"
            onClick={useMyId}
            disabled={!currentUserId}
            data-testid="settings-rbac-user-self"
          >
            使用当前账号
          </Button>
        </div>

        {!activeTargetUserId ? (
          <div
            className="rounded border border-dashed p-4 text-sm text-muted-foreground"
            data-testid="settings-rbac-bindings-no-user"
          >
            未指定目标用户。请填写用户 ID 后点击「查询绑定」。
          </div>
        ) : (
          <BindingsTable
            canManage={canManage}
            roles={roles}
            bindings={bindings}
          />
        )}

        {canManage ? (
          <div className="flex justify-end">
            <Button
              variant="default"
              onClick={openDialog}
              disabled={!activeTargetUserId}
              data-testid="settings-rbac-binding-open-create"
            >
              新增绑定
            </Button>
          </div>
        ) : null}

        {dialogOpen && canManage ? (
          <RoleBindingDialog
            key={`role-binding-dialog-${activeTargetUserId ?? 'none'}-${dialogToken}`}
            presetRoles={roles?.roles ?? []}
            onSubmit={handleCreate}
            onClose={closeDialog}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

interface BindingsTableProps {
  readonly canManage: boolean;
  readonly roles: PresetRoleListResponseDto | null;
  readonly bindings: ReturnType<typeof useUserRoleBindings>;
}

function BindingsTable({ canManage, roles, bindings }: BindingsTableProps): ReactElement {
  const presetByKey = useMemo(() => {
    const map = new Map<string, PresetRoleDto>();
    for (const role of roles?.roles ?? []) map.set(role.key, role);
    return map;
  }, [roles]);

  if (bindings.loading) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="settings-rbac-bindings-loading">
        加载中…
      </div>
    );
  }
  if (bindings.error) {
    return (
      <div className="text-sm text-destructive" data-testid="settings-rbac-bindings-error">
        {bindings.error}
      </div>
    );
  }
  const rows = bindings.data ?? [];
  if (rows.length === 0) {
    return (
      <div
        className="rounded border border-dashed py-6 text-center text-sm text-muted-foreground"
        data-testid="settings-rbac-bindings-empty"
      >
        当前用户暂无角色绑定记录。
      </div>
    );
  }
  return (
    <Table data-testid="settings-rbac-bindings-table">
      <TableHeader>
        <TableRow>
          <TableHead>角色</TableHead>
          <TableHead>数据范围</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>原因</TableHead>
          <TableHead>审批单号</TableHead>
          <TableHead>二次验证</TableHead>
          <TableHead>版本</TableHead>
          <TableHead className="text-right">操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const meta = presetByKey.get(row.roleKey);
          return (
            <TableRow key={row.id} data-testid={`settings-rbac-binding-row-${row.id}`}>
              <TableCell>
                <div className="text-sm">{meta?.displayName ?? row.roleKey}</div>
                <div className="font-mono text-xs text-muted-foreground">{row.roleKey}</div>
              </TableCell>
              <TableCell className="font-mono text-xs">{row.dataScope}</TableCell>
              <TableCell className="text-xs">
                {row.status === 'active' ? '生效中' : '已撤销'}
              </TableCell>
              <TableCell className="max-w-[16rem] truncate text-xs" title={row.reason}>
                {row.reason}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {typeof row.approvalNo === 'string' ? row.approvalNo : '—'}
              </TableCell>
              <TableCell className="text-xs">{row.secondFactorResult}</TableCell>
              <TableCell className="text-right text-xs">{row.version}</TableCell>
              <TableCell className="text-right">
                {canManage && row.status === 'active' ? (
                  <RevokeBindingButton
                    binding={row}
                    isHighRiskRole={meta ? isHighRiskRiskLevel(meta.riskLevel) : true}
                    onRevoke={(input) => bindings.revokeBinding(row.id, input)}
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">只读</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

interface RevokeBindingButtonProps {
  readonly binding: RoleBindingDto;
  /**
   * True when the preset role for this binding has riskLevel high/critical.
   * Caller resolves this via the loaded preset-role catalog. When the catalog
   * has not yet loaded the parent defaults to `true` (fail-closed): we'd
   * rather force the operator to fill approvalNo + secondFactorResult than
   * silently submit a request the backend will reject.
   */
  readonly isHighRiskRole: boolean;
  readonly onRevoke: (input: {
    readonly reason: string;
    readonly approvalNo?: string;
    readonly secondFactorResult?: 'passed';
    readonly version: number;
  }) => Promise<unknown>;
}

function RevokeBindingButton({
  binding,
  isHighRiskRole,
  onRevoke,
}: RevokeBindingButtonProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [approvalNo, setApprovalNo] = useState('');
  const [secondFactorPassed, setSecondFactorPassed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reasonTrim = reason.trim();
  const approvalTrim = approvalNo.trim();
  const requiresApproval = isHighRiskRole;
  const canSubmit =
    !submitting &&
    reasonTrim.length >= 4 &&
    (!requiresApproval || (approvalTrim.length > 0 && secondFactorPassed));

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await onRevoke({
        reason: reasonTrim,
        version: binding.version,
        ...(requiresApproval ? { approvalNo: approvalTrim, secondFactorResult: 'passed' } : {}),
      });
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : '撤销角色绑定失败');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid={`settings-rbac-binding-revoke-${binding.id}`}
      >
        撤销
      </Button>
    );
  }

  return (
    <div
      className="space-y-2 rounded border bg-background p-3 text-left"
      data-testid={`settings-rbac-binding-revoke-form-${binding.id}`}
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor={`revoke-reason-${binding.id}`} className="text-xs">
          撤销原因（≥4 字符）
        </Label>
        <Input
          id={`revoke-reason-${binding.id}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={REVOCATION_REASON_PLACEHOLDER}
          data-testid={`settings-rbac-binding-revoke-reason-${binding.id}`}
        />
      </div>
      {requiresApproval ? (
        <div className="space-y-2 rounded border border-destructive/40 bg-destructive/5 p-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`revoke-approval-${binding.id}`} className="text-xs">
              审批单号
            </Label>
            <Input
              id={`revoke-approval-${binding.id}`}
              value={approvalNo}
              onChange={(e) => setApprovalNo(e.target.value)}
              data-testid={`settings-rbac-binding-revoke-approval-${binding.id}`}
            />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={secondFactorPassed}
              onChange={(e) => setSecondFactorPassed(e.target.checked)}
              data-testid={`settings-rbac-binding-revoke-second-factor-${binding.id}`}
            />
            <span>已完成短信二次验证</span>
          </label>
        </div>
      ) : null}
      {error ? (
        <div
          className="text-xs text-destructive"
          data-testid={`settings-rbac-binding-revoke-error-${binding.id}`}
        >
          {error}
        </div>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOpen(false)}
          data-testid={`settings-rbac-binding-revoke-cancel-${binding.id}`}
        >
          取消
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid={`settings-rbac-binding-revoke-submit-${binding.id}`}
        >
          {submitting ? '提交中…' : '确认撤销'}
        </Button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Channel-partner internal-login policy (read-only, closed in V1)
// -------------------------------------------------------------------------

function ChannelPartnerPolicyCard(): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">渠道商内部后台登录与绑定策略</CardTitle>
        <CardDescription>
          Issue #397 round-5：渠道商可登录受限的 Issue #397 销售漏斗 + 渠道 ROI 汇总入口，
          但渠道身份必须由 super_admin 通过角色绑定流程显式授权；未绑定时下游 channel scope 始终返回零行。
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className="rounded border border-dashed bg-muted/40 p-3 text-sm text-muted-foreground"
          data-testid="settings-rbac-channel-partner-policy"
        >
          当前版本允许 <code>channel_partner</code> 角色登录内部后台，但默认权限为空、仅暴露
          <code> reports</code> 模块；绑定渠道身份须由 super_admin 在角色绑定接口提供 channelPartnerId +
          approvalNo + secondFactorResult=passed，后端会在同一事务中写入
          <code> channel_partner_user_bindings</code> 与审计事件
          <code> channel_partner_user_binding_created</code>。撤销绑定会同步软撤销该行并写入
          <code> channel_partner_user_binding_revoked</code>。
        </div>
      </CardContent>
    </Card>
  );
}
